import { env } from '@/env'
import { hasAcceptedAnalyticsConsent } from '@/shared/analytics/consent/analytics-consent'
import type {
  AnalyticsEventName,
  AnalyticsEventParams,
} from '@/shared/analytics/events'

const YANDEX_METRIKA_ID = env.VITE_YANDEX_METRIKA_ID
const ANALYTICS_DEBUG_ENABLED = env.VITE_ANALYTICS_DEBUG === 'true'
const YANDEX_METRIKA_SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js'
const YANDEX_METRIKA_FALLBACK_SCRIPT_SRC =
  'https://mc.yandex.com/metrika/tag.js'

const isProduction = import.meta.env.PROD

const canUseYandexMetrica =
  Boolean(YANDEX_METRIKA_ID) && (isProduction || ANALYTICS_DEBUG_ENABLED)

let isYandexMetricaInitialized = false

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void
  }
}

const canUseMetrica = (requireConsent: boolean): boolean => {
  return (
    canUseYandexMetrica && (!requireConsent || hasAcceptedAnalyticsConsent())
  )
}

const appendMetricaScript = (src: string): HTMLScriptElement => {
  const script = document.createElement('script')

  script.async = true
  script.src = src
  document.head.appendChild(script)

  return script
}

export const initYandexMetrica = (options?: {
  requireConsent?: boolean
}): boolean => {
  const requireConsent = options?.requireConsent ?? true

  if (
    !canUseMetrica(requireConsent) ||
    !YANDEX_METRIKA_ID ||
    isYandexMetricaInitialized
  ) {
    return false
  }

  if (!window.ym) {
    window.ym = (...args: unknown[]) => {
      ;(window.ym as unknown as { a?: unknown[] }).a =
        (window.ym as unknown as { a?: unknown[] }).a || []
      ;(window.ym as unknown as { a?: unknown[] }).a?.push(args)
    }
    ;(window.ym as unknown as { l?: number }).l = Date.now()
  }

  if (
    !document.querySelector(`script[src="${YANDEX_METRIKA_SCRIPT_SRC}"]`) &&
    !document.querySelector(
      `script[src="${YANDEX_METRIKA_FALLBACK_SCRIPT_SRC}"]`,
    )
  ) {
    const script = appendMetricaScript(YANDEX_METRIKA_SCRIPT_SRC)

    script.addEventListener(
      'error',
      () => {
        script.remove()

        if (
          !document.querySelector(
            `script[src="${YANDEX_METRIKA_FALLBACK_SCRIPT_SRC}"]`,
          )
        ) {
          appendMetricaScript(YANDEX_METRIKA_FALLBACK_SCRIPT_SRC)
        }
      },
      { once: true },
    )
  }

  window.ym(Number(YANDEX_METRIKA_ID), 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  })

  isYandexMetricaInitialized = true

  return true
}

export const trackYandexMetricaPageView = (
  url: string,
  options?: { requireConsent?: boolean },
): void => {
  const requireConsent = options?.requireConsent ?? true

  if (!canUseMetrica(requireConsent) || !window.ym || !YANDEX_METRIKA_ID) {
    return
  }

  window.ym(Number(YANDEX_METRIKA_ID), 'hit', url)
}

export const trackYandexMetricaEvent = (
  eventName: AnalyticsEventName,
  params?: AnalyticsEventParams,
): void => {
  if (!canUseMetrica(true) || !window.ym || !YANDEX_METRIKA_ID) {
    return
  }

  window.ym(Number(YANDEX_METRIKA_ID), 'reachGoal', eventName, params)
}
